import { PageHeader } from '@core/components/PageHeader';
import { SearchBar } from '@core/components/SearchBar';
import Tour from '@core/components/Tour';
import { useApiUtils } from '@core/util/apiUtils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { Button, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useState } from 'react';
import { FaArrowCircleDown, FaChartBar } from 'react-icons/fa';
import { useHistory } from 'react-router';
import { ContasPagarGraficos } from './components';
import ContasPagarBuscaAvancadaModal from './components/modal/ContaPagarBuscaAvancadaModal';
import ImportarContaPagarModal from './components/modal/ImportarContaPagarModal/ImportarContaPagarModal';
import { steps } from './components/steps/ContaPagarSteps';
import { ContasPagarTable } from './container';
import ContasPagarBaixa from './container/ContasPagarBaixa';
import ContasPagarCadastro from './container/ContasPagarCadastro';
import ContasPagarVisualizar from './container/ContasPagarVisualizar';
import ContasPagarRecorrencia from './container/recorrencia/ContasPagarRecorrencia';
import { ContasPagarAction, ContasPagarProvider, useContasPagar } from './store';
import { RecorrenciaConsumer, RecorrenciaProvider } from './store/recorrencia';

const useStyles = makeStyles({
  buttonChartContainer: {
    color: 'white',
    backgroundColor: 'rgba(88, 144, 170, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(88, 144, 170, 1)',
      boxShadow: 'none',
      borderColor: 'rgba(88, 144, 170, 0)',
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: 'rgba(88, 144, 170, 0.9)',
      borderColor: 'rgba(88, 144, 170, 0)',
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgba(88, 144, 170,0.9)',
      borderColor: 'rgba(88, 144, 170, 0)',
    },
  },
  searchBarContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    maxWidth: '512px',
  },
  link: {
    color: '#53c4e6',
    fontWeight: '600',
    textTransform: 'initial',
  },
});

const ContasPagarBusca = () => {
  const [state, dispatch] = useContasPagar();

  const onChangeSearch = (value) => {
    dispatch(ContasPagarAction.setPesquisar(value));
  };

  useEffect(() => {
    const idsSelecionados = state.selecionados.map((selecionado) => selecionado.id);
    ContasPagarAction.buscarSomatorio(dispatch, {
      ids: idsSelecionados,
      query: state.requestParams.q,
      tipoSelecao: state.tipoSelecao,
      ...state.filtroAvancadoFormatado,
    });
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.requestParams.q, state.tipoSelecao]);

  const handleOpenGraficos = () => {
    dispatch(ContasPagarAction.setExibirGraficos(true));
  };

  const styles = useStyles();

  const handleOpenBuscaAvancada = () => {
    dispatch(ContasPagarAction.buscaAvancada());
  };

  return (
    <PageHeader
      title="Contas a Pagar"
      icon={<FaArrowCircleDown />}
      button={
        <Tooltip title="Exibir indicadores">
          <IconButton className={styles.buttonChartContainer} onClick={handleOpenGraficos}>
            <FaChartBar className="stepVisualizarDashboards" />
          </IconButton>
        </Tooltip>
      }
    >
      <div className={styles.searchBarContainer}>
        <div className="flex items-center w-full max-w-512 stepBarraPesquisa">
          <SearchBar searchText={state.search} onChange={onChangeSearch} />
        </div>
        <div className="stepBuscaAvancada">
          <Button className={styles.link} onClick={handleOpenBuscaAvancada}>
            Busca avançada
          </Button>
        </div>
      </div>
    </PageHeader>
  );
};

const ContasPagarContent = () => {
  const history = useHistory();
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;
  const [state, dispatch] = useContasPagar();
  const apiUtils = useApiUtils(dispatch, Actions);

  const [run, setRun] = useState(false);
  const [onTour, setOnTour] = useState(false);
  const [, setLoading] = useState(false);

  useEffect(() => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    onFetchData({
      page: state?.requestParams?.page,
      q: state?.requestParams?.q,
      size: state?.requestParams?.size,
      sort: state?.requestParams?.sort,
    });
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFetchData = (params) => {
    const funcionalidadeJaExistente = funcionalidadesTour?.some((f) => f === 'CONTAS_PAGAR');
    if (!funcionalidadeJaExistente) {
      setRun(true);
      setOnTour(true);
      ContasPagarAction.buscarTour(dispatch);
    } else {
      ContasPagarAction.carregarRegistros(dispatch, params, state.filtroAvancadoFormatado);
    }
  };

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('CONTAS_PAGAR');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setOnTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      onFetchData(state.requestParams);
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FusePageCarded
      innerScroll
      header={<ContasPagarBusca />}
      content={
        <>
          <Tour
            steps={steps}
            run={run}
            setRun={setRun}
            disableScrolling={true}
            onFinish={() => {
              atualizarTour();
            }}
          />
          <ContasPagarBaixa />
          <ContasPagarVisualizar />
          <ContasPagarCadastro />

          <RecorrenciaConsumer>{() => <ContasPagarRecorrencia />}</RecorrenciaConsumer>
          <ContasPagarBuscaAvancadaModal />
          <ContasPagarGraficos />
          <ContasPagarTable onTour={onTour} onFetchData={onFetchData} />
          <ImportarContaPagarModal />
        </>
      }
    />
  );
};

const ContasPagar = () => {
  return (
    <RecorrenciaProvider>
      <ContasPagarProvider>
        <ContasPagarContent />
      </ContasPagarProvider>
    </RecorrenciaProvider>
  );
};

export default ContasPagar;
